import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { io, Socket } from 'socket.io-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { selectAuthToken, selectAuthUser } from '@store/auth/auth.selectors';

export interface ChatRoom {
  idChatRoom: number;
  type: 'DIRECT' | 'GROUP';
  displayName: string;
  otherUserId: number | null;
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastMessageSenderId: number | null;
}

export interface ChatMessage {
  idChatMessage: number;
  idChatRoom: number;
  idSender: number;
  senderEmail: string;
  text: string;
  fileUrl: string | null;
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  createdAt: string;
}

export interface ChatUser {
  idUser: number;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private http = inject(HttpClient);
  private store = inject(Store);
  private socket: Socket | null = null;

  readonly rooms = signal<ChatRoom[]>([]);
  readonly messages = signal<Record<number, ChatMessage[]>>({});
  readonly activeRoomId = signal<number | null>(null);
  readonly users = signal<ChatUser[]>([]);
  readonly unreadCount = signal<Record<number, number>>({});
  readonly connected = signal(false);
  readonly connectionError = signal<string | null>(null);
  readonly uploading = signal(false);
  readonly hasLoginUnread = signal(false);
  readonly panelOpen = signal(false);
  readonly onlineUserIds = signal<Set<number>>(new Set());
  readonly lastIncomingMessage = signal<{ message: ChatMessage; roomId: number } | null>(null);

  private _myUserId: number | null = null;

  private readonly LAST_READ_KEY = 'erp_chat_last_read';

  private getLastReadMap(): Record<number, string> {
    try {
      return JSON.parse(localStorage.getItem(this.LAST_READ_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  markRoomRead(roomId: number): void {
    const map = this.getLastReadMap();
    map[roomId] = new Date().toISOString();
    localStorage.setItem(this.LAST_READ_KEY, JSON.stringify(map));
  }

  private computeInitialUnread(rooms: ChatRoom[]): void {
    const lastRead = this.getLastReadMap();
    const unread: Record<number, number> = { ...this.unreadCount() };
    let hasAny = false;

    for (const room of rooms) {
      if (!room.lastMessageAt) continue;
      if (room.lastMessageSenderId === this._myUserId) continue;
      const lastReadAt = lastRead[room.idChatRoom];
      if (!lastReadAt || new Date(room.lastMessageAt) > new Date(lastReadAt)) {
        if (!unread[room.idChatRoom]) {
          unread[room.idChatRoom] = 1;
        }
        hasAny = true;
      }
    }

    this.unreadCount.set(unread);
    this.hasLoginUnread.set(hasAny);
  }

  readonly activeMessages = computed(() => {
    const id = this.activeRoomId();
    return id !== null ? (this.messages()[id] ?? []) : [];
  });

  readonly totalUnread = computed(() =>
    Object.values(this.unreadCount()).reduce((sum, n) => sum + n, 0)
  );

  private readonly apiBase = environment.apiUrl;
  private readonly socketBase = this.apiBase.replace(/\/api$/, '');

  async connect(): Promise<void> {
    if (this.socket?.connected) return;

    const token = await firstValueFrom(this.store.select(selectAuthToken));
    if (!token) return;

    const user = await firstValueFrom(this.store.select(selectAuthUser));
    this._myUserId = (user as any)?.idUser ?? (user as any)?.id ?? null;

    this.socket?.disconnect();

    this.socket = io(this.socketBase, { auth: { token } });

    this.socket.on('connect', () => {
      console.log('[Chat] Socket conectado:', this.socket?.id);
      this.connected.set(true);
      this.connectionError.set(null);
      this.joinAllRooms();
      this.socket?.emit('chat:get-online');
    });

    this.socket.on('connect_error', (err) => {
      console.error('[Chat] Error de conexión:', err.message);
      this.connectionError.set(err.message);
      this.connected.set(false);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Chat] Socket desconectado:', reason);
      this.connected.set(false);
    });

    this.socket.on('chat:message', ({ roomId, message }: { roomId: number; message: ChatMessage }) => {
      const id = Number(roomId);

      this.messages.update(all => ({
        ...all,
        [id]: [...(all[id] ?? []), message],
      }));

      const knownRoom = this.rooms().find(r => r.idChatRoom === id);

      if (knownRoom) {
        this.rooms.update(rooms =>
          rooms.map(r => r.idChatRoom === id
            ? { ...r, lastMessage: message.text, lastMessageAt: message.createdAt }
            : r
          )
        );
      } else {
        // Sala nueva: recargar la lista para que aparezca
        this.loadRooms();
      }

      if (this.activeRoomId() !== id && message.idSender !== this._myUserId) {
        this.unreadCount.update(u => ({ ...u, [id]: (u[id] ?? 0) + 1 }));
        this.lastIncomingMessage.set({ message, roomId: id });
      }
    });

    this.socket.on('chat:error', ({ message }: { message: string }) => {
      console.error('[Chat] Error del servidor:', message);
    });

    this.socket.on('chat:online-users', ({ ids }: { ids: number[] }) => {
      this.onlineUserIds.set(new Set(ids));
    });

    this.socket.on('chat:user-online', ({ idUser }: { idUser: number }) => {
      this.onlineUserIds.update(s => new Set([...s, idUser]));
    });

    this.socket.on('chat:user-offline', ({ idUser }: { idUser: number }) => {
      this.onlineUserIds.update(s => { const n = new Set(s); n.delete(idUser); return n; });
    });
  }

  private joinAllRooms(): void {
    for (const room of this.rooms()) {
      this.socket?.emit('chat:join', { roomId: room.idChatRoom });
      console.log('[Chat] Auto-join room:', room.idChatRoom);
    }
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connected.set(false);
  }

  async loadRooms(): Promise<void> {
    try {
      const token = await firstValueFrom(this.store.select(selectAuthToken));
      const res = await firstValueFrom(
        this.http.get<{ ok: boolean; data: ChatRoom[] }>(`${this.apiBase}/chat/rooms`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      this.rooms.set(res.data);
      this.computeInitialUnread(res.data);
      this.joinAllRooms();
    } catch (err) {
      console.error('[Chat] Error cargando salas:', err);
    }
  }

  async loadUsers(): Promise<void> {
    try {
      const token = await firstValueFrom(this.store.select(selectAuthToken));
      const res = await firstValueFrom(
        this.http.get<{ ok: boolean; data: ChatUser[] }>(`${this.apiBase}/chat/users`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      this.users.set(res.data);
    } catch (err) {
      console.error('[Chat] Error cargando usuarios:', err);
    }
  }

  async openDirectChat(targetUserId: number): Promise<void> {
    const token = await firstValueFrom(this.store.select(selectAuthToken));
    const res = await firstValueFrom(
      this.http.post<{ ok: boolean; data: { idChatRoom: number } }>(
        `${this.apiBase}/chat/rooms/direct`,
        { targetUserId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
    this.socket?.emit('chat:join', { roomId: res.data.idChatRoom });
    await this.loadRooms();
    await this.openRoom(res.data.idChatRoom);
  }

  async openRoom(roomId: number): Promise<void> {
    this.activeRoomId.set(roomId);
    this.unreadCount.update(u => ({ ...u, [roomId]: 0 }));
    this.markRoomRead(roomId);
    this.socket?.emit('chat:join', { roomId });
    console.log('[Chat] Joining room:', roomId, '| socket connected:', this.socket?.connected);

    if (!this.messages()[roomId]) {
      try {
        const token = await firstValueFrom(this.store.select(selectAuthToken));
        const res = await firstValueFrom(
          this.http.get<{ ok: boolean; data: ChatMessage[] }>(
            `${this.apiBase}/chat/rooms/${roomId}/messages`,
            { headers: { Authorization: `Bearer ${token}` } }
          )
        );
        this.messages.update(all => ({ ...all, [roomId]: res.data }));
      } catch (err) {
        console.error('[Chat] Error cargando mensajes:', err);
      }
    }
  }

  sendMessage(text: string): void {
    const roomId = this.activeRoomId();
    if (!roomId || !text.trim()) return;
    console.log('[Chat] Enviando mensaje a room:', roomId, '| connected:', this.socket?.connected);
    this.socket?.emit('chat:send', { roomId, text: text.trim() });
  }

  async uploadFile(roomId: number, file: File): Promise<void> {
    this.uploading.set(true);
    try {
      const token = await firstValueFrom(this.store.select(selectAuthToken));
      const form = new FormData();
      form.append('file', file);
      await firstValueFrom(
        this.http.post(
          `${this.apiBase}/chat/rooms/${roomId}/upload`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      // El mensaje llega via socket — no hace falta actualizar el signal manualmente
    } catch (err) {
      console.error('[Chat] Error subiendo archivo:', err);
      throw err;
    } finally {
      this.uploading.set(false);
    }
  }

  closeRoom(): void {
    this.activeRoomId.set(null);
  }

  async currentUserId(): Promise<number | null> {
    const user = await firstValueFrom(this.store.select(selectAuthUser));
    return (user as any)?.idUser ?? (user as any)?.id ?? null;
  }
}
